import React from "react";
import {
  Stack,
  Title,
  Text,
  Button,
  Group,
  Card,
  Divider,
  Paper,
  Alert,
  Badge,
  List,
  Loader,
} from "@mantine/core";
import { highlightVariables } from "../../utils/messageUtils";
import { useCampaign } from "../../context/CampaignContext";
import { useSalon } from "../../context/SalonContext";
import moment from "moment-timezone";

function FinalReviewStep({ date, onBack, onSubmit }) {
  const {
    name,
    description,
    message,
    complianceWarnings: warnings,
    selectedClientIds,
    selectedClientGroupIds,
    campaignCreationLoading,
    isScheduled,
    scheduledDate,
    scheduledTime,
    allClients,
  } = useCampaign();

  const { businessInfo } = useSalon();
  const selectedClients = allClients.filter((c) =>
    selectedClientIds.includes(c.id),
  );
  const recipientCount = selectedClients.length;
  const showNames = selectedClients.slice(0, 4);
  const moreCount = recipientCount > 4 ? recipientCount - 4 : 0;
  const groupCount = selectedClientGroupIds?.length || 0;

  const formattedScheduledTime = isScheduled
    ? moment
        .tz(scheduledDate, businessInfo.timezone)
        .hour(moment(scheduledTime, "h:mm A").hour())
        .minute(moment(scheduledTime, "h:mm A").minute())
        .format("MMM D, YYYY h:mm A")
    : null;

  return (
    <Stack gap="md">
      <Title order={3}>Final Review</Title>
      <Card withBorder radius="md" shadow="xs" p="md">
        <Stack gap="xs">
          <Group>
            <Text fw={500}>Date:</Text>
            <Text>{isScheduled ? formattedScheduledTime : date}</Text>
          </Group>
          <Group>
            <Text fw={500}>Campaign Name:</Text>
            <Text>{name}</Text>
          </Group>
          <Group>
            <Text fw={500}>Description:</Text>
            <Text>{description}</Text>
          </Group>
          <Divider my="sm" />
          <Text fw={500} mb={2}>
            Recipients ({recipientCount}
            {recipientCount === 1 ? " client" : " clients"} selected
            {groupCount > 0
              ? ` + ${groupCount} client group${groupCount === 1 ? "" : "s"}`
              : ""}
            ):
          </Text>
          <List size="sm" spacing={2}>
            {showNames.map((c, i) => (
              <List.Item key={c.id || i}>
                {c.first_name} {c.last_name}
              </List.Item>
            ))}
            {moreCount > 0 && (
              <List.Item key="more">and {moreCount} more</List.Item>
            )}
          </List>
          {groupCount > 0 && (
            <Text size="xs" c="dimmed">
              {groupCount} client group{groupCount === 1 ? "" : "s"} targeted —
              members are resolved when the campaign sends.
            </Text>
          )}
          <Divider my="sm" />
          <Text fw={500} mb={2}>
            Your Message Template:
          </Text>
          <Paper withBorder p="sm" radius="md">
            <Text
              size="sm"
              style={{ fontFamily: "monospace", whiteSpace: "pre-line" }}
            >
              {highlightVariables(message)}
            </Text>
          </Paper>
          {warnings && warnings.length > 0 && (
            <Alert color="yellow" title="Warnings" mt="md">
              <List size="sm" spacing={2}>
                {warnings.map((w, i) => (
                  <List.Item key={i}>{w}</List.Item>
                ))}
              </List>
            </Alert>
          )}
        </Stack>
      </Card>
      <Group justify="space-between" mt="md">
        <Button variant="default" onClick={onBack}>
          &larr; Back
        </Button>
        <Button
          color="teal"
          onClick={onSubmit}
          loading={campaignCreationLoading}
          disabled={campaignCreationLoading}
        >
          {campaignCreationLoading ? "Creating Campaign..." : "Create Campaign"}
        </Button>
      </Group>
    </Stack>
  );
}

export default FinalReviewStep;
